import React, { useState, useEffect, useCallback } from 'react';
import { Heart, MessageCircle, Sparkles, Trash2, RefreshCw, Eye, EyeOff, Download, Star, Gift, Mic, Play, Pause } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import ExportPDFModal from './ExportPDFModal';
import { useTheme } from '../../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_REVEAL_NOTES = 8;
const MAX_REVEAL_VOICES = 5;

/**
 * LoveGallery Component
 * 
 * Phase 4: Couple's Experience
 * - Masonry grid layout with table tagging
 * - Morning After Reveal cover experience
 * - Star feature for favorite wishes
 * - Filter by table and type
 * - PDF Export with Heirloom & Complete editions
 * - Curate reveal notes (max 8 with heart action)
 */
export function LoveGallery({ eventId, eventName, eventDate, eventLocation, token }) {
  const { adminDarkMode, adminTheme: th } = useTheme();
  const [wishes, setWishes] = useState([]);
  const [stats, setStats] = useState({ 
    total_wishes: 0, 
    total_hearts: 0, 
    total_messages: 0, 
    revealed: false,
    starred_count: 0 
  });
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'hearts', 'messages', 'starred'
  const [tableFilter, setTableFilter] = useState('all');
  const [showRevealCover, setShowRevealCover] = useState(false);
  const [revealAnimationStep, setRevealAnimationStep] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  // Reveal curation (message notes only)
  const [revealNotesOrder, setRevealNotesOrder] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  // Voice notes — unified curation (text + voice mixed)
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [revealLoveOrder, setRevealLoveOrder] = useState([]); // [{type:'note'|'voice', id}]
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioRefMap = React.useRef({});

  // Get unique table names from wishes
  const uniqueTables = [...new Set(wishes.map(w => w.table_name).filter(Boolean))].sort();

  useEffect(() => {
    fetchWishes();
    fetchStats();
    fetchRevealNotesOrder();
    fetchVoiceNotes();
    fetchRevealLoveOrder();
  }, [eventId]);

  const fetchVoiceNotes = useCallback(async () => {
    setVoiceLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/voice-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVoiceNotes(res.data || []);
    } catch { /* noop */ }
    finally { setVoiceLoading(false); }
  }, [eventId, token]);

  const fetchRevealLoveOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/reveal-love-order`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRevealLoveOrder(res.data?.order || []);
    } catch { /* noop */ }
  }, [eventId, token]);

  // Build & persist the unified mixed reveal order based on current selections
  // (text-heart selections + voice-heart selections), chronologically interleaved.
  const persistLoveOrder = async (textIds, voiceIds) => {
    const textWithTs = textIds
      .map(id => {
        const w = wishes.find(x => x.id === id);
        return w ? { type: 'note', id, ts: new Date(w.created_at).getTime() } : null;
      })
      .filter(Boolean);
    const voiceWithTs = voiceIds
      .map(id => {
        const v = voiceNotes.find(x => x.id === id);
        return v ? { type: 'voice', id, ts: new Date(v.created_at).getTime() } : null;
      })
      .filter(Boolean);
    const merged = [...textWithTs, ...voiceWithTs]
      .sort((a, b) => a.ts - b.ts)
      .map(({ type, id }) => ({ type, id }));
    setRevealLoveOrder(merged);
    try {
      await axios.put(`${API}/events/${eventId}/reveal-love-order`,
        { order: merged },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* */ }
  };

  const fetchRevealNotesOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/reveal-notes-order`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRevealNotesOrder(res.data?.order || []);
    } catch { /* noop */ }
  }, [eventId, token]);

  const toggleRevealNote = async (wishId) => {
    const isIn = revealNotesOrder.includes(wishId);
    let next;
    if (isIn) {
      next = revealNotesOrder.filter(id => id !== wishId);
    } else {
      if (revealNotesOrder.length >= MAX_REVEAL_NOTES) {
        toast.message(`You can select up to ${MAX_REVEAL_NOTES} notes`);
        return;
      }
      next = [...revealNotesOrder, wishId];
    }
    setRevealNotesOrder(next); // optimistic
    setSavingOrder(true);
    try {
      await axios.put(`${API}/events/${eventId}/reveal-notes-order`,
        { order: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mirror into unified mixed order
      const voiceIds = revealLoveOrder.filter(x => x.type === 'voice').map(x => x.id);
      await persistLoveOrder(next, voiceIds);
    } catch {
      // rollback
      setRevealNotesOrder(revealNotesOrder);
      toast.error('Could not save selection');
    } finally {
      setSavingOrder(false);
    }
  };

  const toggleRevealVoice = async (voiceId) => {
    const currentVoiceIds = revealLoveOrder.filter(x => x.type === 'voice').map(x => x.id);
    const isIn = currentVoiceIds.includes(voiceId);
    let nextVoiceIds;
    if (isIn) {
      nextVoiceIds = currentVoiceIds.filter(id => id !== voiceId);
    } else {
      if (currentVoiceIds.length >= MAX_REVEAL_VOICES) {
        toast.message(`You can select up to ${MAX_REVEAL_VOICES} voices`);
        return;
      }
      nextVoiceIds = [...currentVoiceIds, voiceId];
    }
    await persistLoveOrder(revealNotesOrder, nextVoiceIds);
  };

  const toggleVoiceVisible = async (voiceId, currentVisible) => {
    // Soft filter — hide awkward voice from reveal entirely
    try {
      await axios.patch(`${API}/events/${eventId}/voice-notes/${voiceId}`,
        { included_in_reveal: !currentVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoiceNotes(prev => prev.map(v =>
        v.id === voiceId ? { ...v, included_in_reveal: !currentVisible } : v
      ));
    } catch { toast.error('Could not update'); }
  };

  const handleDeleteVoice = async (voiceId) => {
    if (!window.confirm('Delete this voice message?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}/voice-notes/${voiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVoiceNotes(prev => prev.filter(v => v.id !== voiceId));
      // If it was in reveal, drop it
      const newOrder = revealLoveOrder.filter(x => !(x.type === 'voice' && x.id === voiceId));
      setRevealLoveOrder(newOrder);
    } catch { toast.error('Delete failed'); }
  };

  const playPauseVoice = (voiceId) => {
    const a = audioRefMap.current[voiceId];
    if (!a) return;
    if (playingVoiceId === voiceId) {
      a.pause();
      setPlayingVoiceId(null);
      return;
    }
    // Pause any other
    Object.entries(audioRefMap.current).forEach(([id, el]) => {
      if (id !== voiceId && el) { try { el.pause(); el.currentTime = 0; } catch { /* */ } }
    });
    a.play().then(() => setPlayingVoiceId(voiceId)).catch(() => setPlayingVoiceId(null));
  };

  const fmtDuration = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = Math.max(0, Math.floor((s || 0) % 60));
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const fetchWishes = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/wishes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishes(response.data);
    } catch (error) {
      console.error('Failed to fetch wishes:', error);
      toast.error('Failed to load wishes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/wishes/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleReveal = async () => {
    if (!stats.revealed) {
      // Show the "Morning After Reveal" cover experience
      setShowRevealCover(true);
      setRevealAnimationStep(1);
      
      // Animate through steps
      setTimeout(() => setRevealAnimationStep(2), 1500);
      setTimeout(() => setRevealAnimationStep(3), 3000);
      setTimeout(async () => {
        setRevealing(true);
        try {
          await axios.post(`${API}/events/${eventId}/wishes/reveal`, 
            { reveal: true },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setStats(prev => ({ ...prev, revealed: true }));
          await fetchWishes();
          toast.success('Wishes revealed! The magic moment is here!');
        } catch (error) {
          toast.error('Failed to reveal wishes');
        } finally {
          setRevealing(false);
          setTimeout(() => setShowRevealCover(false), 1000);
        }
      }, 4500);
    } else {
      // Just hide wishes
      setRevealing(true);
      try {
        await axios.post(`${API}/events/${eventId}/wishes/reveal`, 
          { reveal: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStats(prev => ({ ...prev, revealed: false }));
        await fetchWishes();
        toast.success('Wishes hidden');
      } catch (error) {
        toast.error('Failed to hide wishes');
      } finally {
        setRevealing(false);
      }
    }
  };

  const handleToggleStar = async (wishId) => {
    try {
      const response = await axios.put(`${API}/events/${eventId}/wishes/${wishId}/star`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setWishes(prev => prev.map(w => 
        w.id === wishId ? { ...w, is_starred: response.data.is_starred } : w
      ));
      fetchStats();
    } catch (error) {
      toast.error('Failed to update wish');
    }
  };

  const handleDeleteWish = async (wishId) => {
    if (!window.confirm('Are you sure you want to delete this wish?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}/wishes/${wishId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishes(prev => prev.filter(w => w.id !== wishId));
      toast.success('Wish deleted');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete wish');
    }
  };

  const filteredWishes = wishes.filter(w => {
    // Filter by type
    if (filter === 'hearts' && w.wish_type !== 'heart') return false;
    if (filter === 'messages' && w.wish_type !== 'message') return false;
    if (filter === 'starred' && !w.is_starred) return false;
    // Filter by table
    if (tableFilter !== 'all' && w.table_name !== tableFilter) return false;
    return true;
  });

  // Morning After Reveal Cover Experience
  if (showRevealCover) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center p-8 max-w-lg mx-auto">
          {revealAnimationStep >= 1 && (
            <div className="animate-in fade-in duration-1000">
              <div className="mb-8">
                <Gift className="w-16 h-16 mx-auto text-amber-400 animate-pulse" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-white mb-4">
                {eventName || 'Your Special Day'}
              </h1>
              {eventDate && (
                <p className="text-slate-400 text-lg">
                  {new Date(eventDate).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                  {eventLocation && ` • ${eventLocation}`}
                </p>
              )}
            </div>
          )}
          
          {revealAnimationStep >= 2 && (
            <div className="mt-12 animate-in fade-in slide-in-from-bottom duration-1000">
              <p className="text-xl text-slate-300 font-light italic mb-8">
                While you were celebrating...
              </p>
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-4xl font-bold text-amber-400">{stats.total_wishes}</p>
                  <p className="text-sm text-slate-400 mt-1">guests sent love</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-pink-400">{stats.total_hearts}</p>
                  <p className="text-sm text-slate-400 mt-1">hearts</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-blue-400">{stats.total_messages}</p>
                  <p className="text-sm text-slate-400 mt-1">wishes written</p>
                </div>
              </div>
            </div>
          )}
          
          {revealAnimationStep >= 3 && (
            <div className="mt-12 animate-in fade-in duration-500">
              <p className="text-slate-400 mb-4">Opening your guestbook...</p>
              <div className="w-12 h-12 mx-auto border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-pink-500/20">
                <Heart className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold"
                  style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {stats.total_hearts}
                </p>
                <p 
                  className="text-xs"
                  style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  Hearts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-500/20">
                <MessageCircle className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold"
                  style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {stats.total_messages}
                </p>
                <p 
                  className="text-xs"
                  style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  Messages
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-500/20">
                <Sparkles className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold"
                  style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {stats.total_wishes}
                </p>
                <p 
                  className="text-xs"
                  style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  Total
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-yellow-500/20">
                <Star className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold"
                  style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {stats.starred_count || 0}
                </p>
                <p 
                  className="text-xs"
                  style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  Starred
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${stats.revealed ? 'bg-green-500/20' : 'bg-slate-700/50'}`}>
                {stats.revealed ? <Eye className="h-5 w-5 text-green-400" /> : <EyeOff className="h-5 w-5 text-slate-400" />}
              </div>
              <div>
                <p 
                  className="text-lg font-bold"
                  style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {stats.revealed ? 'Revealed' : 'Hidden'}
                </p>
                <p 
                  className="text-xs"
                  style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  Status
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'border-slate-700'}
          >
            All
          </Button>
          <Button
            variant={filter === 'hearts' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('hearts')}
            className={filter === 'hearts' ? 'bg-pink-500 hover:bg-pink-600' : 'border-slate-700'}
          >
            <Heart className="h-4 w-4 mr-1" /> Hearts
          </Button>
          <Button
            variant={filter === 'messages' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('messages')}
            className={filter === 'messages' ? 'bg-blue-500 hover:bg-blue-600' : 'border-slate-700'}
          >
            <MessageCircle className="h-4 w-4 mr-1" /> Messages
          </Button>
          <Button
            variant={filter === 'starred' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('starred')}
            className={filter === 'starred' ? 'bg-yellow-500 hover:bg-yellow-600 text-slate-900' : 'border-slate-700'}
          >
            <Star className="h-4 w-4 mr-1" /> Starred
          </Button>
          
          {/* Table Filter Dropdown */}
          {uniqueTables.length > 0 && (
            <select
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="px-3 py-1.5 rounded-md text-sm bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Tables</option>
              {uniqueTables.map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchWishes(); fetchStats(); }}
            className="border-slate-700"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          
          <Button
            onClick={handleReveal}
            disabled={revealing}
            className={stats.revealed 
              ? 'bg-slate-700 hover:bg-slate-600' 
              : 'bg-gradient-to-r from-pink-500 to-amber-500 hover:from-pink-600 hover:to-amber-600'
            }
            data-testid="reveal-wishes-btn"
          >
            {revealing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : stats.revealed ? (
              <EyeOff className="h-4 w-4 mr-2" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {stats.revealed ? 'Hide Wishes' : 'Morning After Reveal'}
          </Button>
        </div>
      </div>

      {/* Reveal curation helper (subtle, above grid) */}
      {wishes.some(w => w.wish_type === 'message') && (
        <div
          className="flex items-center justify-between px-1 -mb-2"
          data-testid="notes-curate-bar"
        >
          <p
            className="text-[12px]"
            style={{ color: adminDarkMode ? '#94a3b8' : '#64748b', fontVariantNumeric: 'tabular-nums' }}
          >
            {revealNotesOrder.length > 0 ? (
              <>
                <span data-testid="notes-curate-counter">{revealNotesOrder.length} / {MAX_REVEAL_NOTES}</span>
                <span className="ml-1">selected for reveal</span>
              </>
            ) : (
              <span className="italic opacity-80">Tap the heart on a message to include it in your reveal · up to {MAX_REVEAL_NOTES}</span>
            )}
          </p>
          {revealNotesOrder.length > 0 && (
            <button
              onClick={async () => {
                setRevealNotesOrder([]);
                try {
                  await axios.put(`${API}/events/${eventId}/reveal-notes-order`,
                    { order: [] },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                } catch { /* */ }
              }}
              className="text-[12px] transition-colors hover:opacity-70"
              style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}
              data-testid="notes-curate-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Wishes Grid (Masonry-like) */}
      {filteredWishes.length === 0 ? (
        <Card 
          className={adminDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}
          style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
        >
          <CardContent className="py-12 text-center">
            <Heart className="h-12 w-12 mx-auto mb-4 text-slate-600" />
            <h3 
              className="text-lg font-medium mb-2"
              style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
            >
              No wishes yet
            </h3>
            <p style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}>
              Wishes from your guests will appear here as they send them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWishes.map((wish) => {
            const isInReveal = wish.wish_type === 'message' && revealNotesOrder.includes(wish.id);
            return (
            <Card 
              key={wish.id} 
              className={`relative transition-all ${adminDarkMode ? 'bg-slate-900/50 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 hover:border-slate-300'} ${
                wish.wish_type === 'heart' ? 'border-l-4 border-l-pink-500' : 'border-l-4 border-l-blue-500'
              } ${wish.is_starred ? 'ring-1 ring-yellow-500/50' : ''} ${isInReveal ? 'ring-1 ring-pink-400/60' : ''}`}
              style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
              data-testid={`wish-card-${wish.id}`}
            >
              <CardContent className="pt-4">
                {/* Action buttons */}
                <div className="absolute top-2 right-2 flex gap-1">
                  {wish.wish_type === 'message' && (
                    <button
                      onClick={() => toggleRevealNote(wish.id)}
                      disabled={savingOrder}
                      className={`p-1.5 rounded-lg transition-all ${
                        isInReveal
                          ? 'text-pink-400 bg-pink-500/15'
                          : 'text-slate-500 hover:text-pink-400 hover:bg-pink-500/10'
                      }`}
                      title={isInReveal ? 'Remove from reveal' : 'Include in reveal'}
                      data-testid={`wish-reveal-toggle-${wish.id}`}
                    >
                      <Heart className={`h-4 w-4 ${isInReveal ? 'fill-current' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleStar(wish.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      wish.is_starred 
                        ? 'text-yellow-400 bg-yellow-500/20' 
                        : 'text-slate-500 hover:text-yellow-400 hover:bg-yellow-500/10'
                    }`}
                    title={wish.is_starred ? 'Unstar' : 'Star this wish'}
                  >
                    <Star className={`h-4 w-4 ${wish.is_starred ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleDeleteWish(wish.id)}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete wish"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Wish type icon */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${
                    wish.wish_type === 'heart' ? 'bg-pink-500/20' : 'bg-blue-500/20'
                  }`}>
                    {wish.wish_type === 'heart' ? (
                      <Heart className="h-5 w-5 text-pink-400 fill-pink-400" />
                    ) : (
                      <MessageCircle className="h-5 w-5 text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p 
                      className="font-medium truncate font-serif"
                      style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                    >
                      {wish.guest_name}
                    </p>
                    <div 
                      className="flex items-center gap-2 text-xs"
                      style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: '#64748b' }}
                    >
                      <span>{new Date(wish.created_at).toLocaleString()}</span>
                      {wish.table_name && (
                        <>
                          <span>•</span>
                          <span style={th?.useInlineStyles ? { color: th.colors.accent } : { color: '#f59e0b' }}>{wish.table_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Message content */}
                {wish.wish_type === 'message' && wish.message && (
                  <p 
                    className="text-sm leading-relaxed whitespace-pre-wrap font-serif italic"
                    style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#475569' }}
                  >
                    "{wish.message}"
                  </p>
                )}
                
                {wish.wish_type === 'heart' && (
                  <p className="text-pink-400 text-sm italic">Sent love!</p>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* ─── Voice Notes (In their voices) ─────────────────── */}
      {voiceNotes.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <Mic className={`h-4 w-4 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
              <p
                className="text-sm font-medium"
                style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
              >
                In their voices
              </p>
              <span
                className="text-[12px]"
                style={{ color: adminDarkMode ? '#94a3b8' : '#64748b', fontVariantNumeric: 'tabular-nums' }}
                data-testid="voices-curate-counter"
              >
                {revealLoveOrder.filter(x => x.type === 'voice').length} / {MAX_REVEAL_VOICES} for reveal
              </span>
            </div>
            <p
              className="text-[11px] italic"
              style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}
            >
              Tap ♥ to include · eye to hide
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {voiceNotes.map((v) => {
              const inReveal = revealLoveOrder.some(x => x.type === 'voice' && x.id === v.id);
              const visible = v.included_in_reveal !== false;
              const isPlaying = playingVoiceId === v.id;
              const audioFullUrl = `${process.env.REACT_APP_BACKEND_URL}${v.audio_url}`;
              return (
                <div
                  key={v.id}
                  className={`relative p-4 rounded-lg border transition-all ${
                    adminDarkMode ? 'bg-slate-900/50 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 hover:border-slate-300'
                  } ${inReveal ? 'ring-1 ring-pink-400/60' : ''} ${!visible ? 'opacity-50' : ''}`}
                  style={th?.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
                  data-testid={`voice-card-${v.id}`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => playPauseVoice(v.id)}
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        isPlaying ? 'bg-pink-500 text-white' : 'bg-slate-200/10 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                      title={isPlaying ? 'Pause' : 'Play'}
                      data-testid={`voice-play-${v.id}`}
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium truncate font-serif"
                        style={th?.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                      >
                        In {v.guest_name}&rsquo;s voice
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: adminDarkMode ? '#94a3b8' : '#64748b', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmtDuration(v.duration_sec)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleRevealVoice(v.id)}
                        disabled={!visible}
                        className={`p-1.5 rounded-lg transition-all ${
                          inReveal
                            ? 'text-pink-400 bg-pink-500/15'
                            : 'text-slate-500 hover:text-pink-400 hover:bg-pink-500/10'
                        } disabled:opacity-30 disabled:cursor-not-allowed`}
                        title={inReveal ? 'Remove from reveal' : 'Include in reveal'}
                        data-testid={`voice-reveal-toggle-${v.id}`}
                      >
                        <Heart className={`h-4 w-4 ${inReveal ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => toggleVoiceVisible(v.id, visible)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                        title={visible ? 'Hide from reveal' : 'Show in reveal'}
                        data-testid={`voice-visibility-${v.id}`}
                      >
                        {visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteVoice(v.id)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete voice message"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <audio
                    ref={(el) => { audioRefMap.current[v.id] = el; }}
                    src={audioFullUrl}
                    onEnded={() => setPlayingVoiceId(null)}
                    preload="metadata"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PDF Export Section */}
      {stats.total_wishes > 0 && (
        <div 
          className={`pt-4 border-t ${adminDarkMode ? 'border-slate-800' : 'border-slate-200'}`}
          style={th?.useInlineStyles ? { borderColor: th.colors.border } : {}}
        >
          <p 
            className="text-sm mb-3"
            style={th?.useInlineStyles ? { color: th.colors.textSecondary } : { color: '#64748b' }}
          >
            Export Options
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowExportModal(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Love Gallery
            </Button>
          </div>
        </div>
      )}
      
      {/* Export PDF Modal */}
      <ExportPDFModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        eventId={eventId}
        eventName={eventName}
        token={token}
        stats={stats}
      />
    </div>
  );
}

export default LoveGallery;
